'use strict';

const HEADER_SLOT_ORDER = [
  'headerBrandLeft',
  'headerBrand',
  'headerSidebarToggle',
  'headerLeft',
  'headerCenter',
  'headerRight',
  'logout'
];

const SIDEBAR_SLOT_ORDER = [
  'sidebarLeftTop',
  'sidebarLeftBottom',
  'sidebarLeftFooter'
];

const PAGE_SLOTS = new Set(['formHeader', 'formFooter']);

const KNOWN_CHROME_SLOTS = new Set([
  ...HEADER_SLOT_ORDER,
  'header',
  ...SIDEBAR_SLOT_ORDER,
  'appFooter'
]);

// Editable Tailwind on the BF field (FormEdit Classes). Layout lives in the
// schema (App Shell / Body), not in basecode grid CSS.
const TW_APP_SHELL = 'flex h-screen flex-col overflow-hidden';
const TW_BODY = 'relative flex min-h-0 flex-1';
const TW_HEADER_BAR = 'relative z-[200] flex h-[60px] w-full shrink-0 items-center bg-white';
const TW_HEADER_CELL = 'flex h-full items-center';
const TW_HEADER_BRAND = 'relative flex h-[60px] w-[240px] shrink-0 items-center pl-2.5';
const TW_HEADER_NAV = 'm-0 flex h-full shrink-0 list-none items-center p-0';
const TW_HEADER_NAV_RIGHT = 'm-0 ml-auto flex h-full shrink-0 list-none items-center p-0';
const TW_LOGO_LINK = 'inline-block pl-2.5 text-[1.188em] uppercase text-[#1d212a]';
const TW_HEADER_ICON_BTN = 'flex h-[60px] w-[60px] cursor-pointer items-center justify-center border-0 bg-transparent p-5 text-lg';
const TW_LOGOUT_BTN = 'flex h-[60px] w-auto cursor-pointer items-center gap-1 border-0 bg-transparent px-3 text-lg';
const TW_SECONDARY_HEADER = 'w-full min-h-0 shrink-0';
// .sidebar / .sidebar-left keep theme + nav item colors from the neu pack.
// Neu .sidebar position/size rules are unlayered and beat Tailwind v4
// layered utilities, so those properties use important classes.
const TW_SIDEBAR = 'sidebar sidebar-left !w-60 shrink-0 overflow-y-auto';
const TW_PAGE_BODY = 'mb-0 min-w-0 flex-1 overflow-auto bg-[#F0F3F3]';
const TW_APP_FOOTER = 'shrink-0';

// Reactive width / mobile slide-out. Becomes a styleClasses getter at runtime.
const STYLE_CLASSES_SIDEBAR_CALC = [
  '(function () {',
  "  var mini = vueapp.$store.state.site.sidebarMini;",
  "  var open = vueapp.$store.state.site.sidebarOpen;",
  "  var base = 'sidebar sidebar-left shrink-0 overflow-y-auto';",
  "  var desktop = mini ? 'md:!w-[50px]' : 'md:!w-60';",
  "  var mobile = open",
  "    ? '!absolute !inset-y-0 !left-0 z-[150] !w-60 md:!static md:z-auto'",
  "    : '!absolute !inset-y-0 !left-0 z-[150] !w-60 -translate-x-full md:!static md:z-auto md:translate-x-0';",
  "  return [base, desktop, mobile].join(' ');",
  '})()'
].join('');

const HOISTABLE_TAGS = new Set([
  'div', 'span', 'li', 'nav', 'section', 'header', 'footer', 'article', 'ul', 'ol', 'p'
]);

const DEFAULT_HEADER_BRAND_HTML = `<router-link tag="a" to="/" class="${TW_LOGO_LINK}" v-html="$store.state.site.appName"></router-link>`;

const DEFAULT_HEADER_TOGGLE_HTML = [
  `<button type="button" class="${TW_HEADER_ICON_BTN}" id="toggle-left" @click.prevent="`,
  '(function () { var w = vueapp; if (window.$ && window.$(window).width() > 767) {',
  'w.$store.commit(\'SET_SIDEBARMINI\', !w.$store.state.site.sidebarMini);',
  '} else { w.$store.commit(\'SET_SIDEBAROPEN\', !w.$store.state.site.sidebarOpen); }',
  '})()">',
  '<i class="fa fa-bars"></i>',
  '</button>'
].join('');

const DEFAULT_LOGOUT_HTML = [
  '<button type="button" @click.prevent="vueapp.$store.dispatch(\'LOGOUT\'); vueapp.$router.push(\'/login\')"',
  ` class="${TW_LOGOUT_BTN}">`,
  '<i class="fa fa-sign-out"></i>',
  '<span class="hidden sm:inline">Logout</span>',
  '</button>'
].join('');

const VISIBLE_TOP_NAV = 'vueapp.$store.getters.showTopNavBar';
const VISIBLE_SIDE_NAV = 'vueapp.$store.getters.showSideNavBar';
const VISIBLE_AUTH = 'vueapp.$store.state.auth.authenticated';
const VISIBLE_FALSE = 'false';

const DEFAULT_FIELD_TEMPLATES = {
  headerBrand: {
    label: 'headerBrand',
    html: DEFAULT_HEADER_BRAND_HTML,
    styleClasses: TW_HEADER_BRAND
  },
  headerSidebarToggle: {
    label: 'headerSidebarToggle',
    html: DEFAULT_HEADER_TOGGLE_HTML,
    styleClasses: TW_HEADER_CELL,
    visible_calc: VISIBLE_SIDE_NAV
  },
  logout: {
    label: 'logout',
    html: DEFAULT_LOGOUT_HTML,
    styleClasses: TW_HEADER_CELL,
    visible_calc: VISIBLE_AUTH
  }
};

function normalizeSiteInput(siteInput) {
  if (!siteInput || typeof siteInput !== 'object') {
    return { site: {}, content: { slots: [], navigation: [] } };
  }

  if (siteInput.content && typeof siteInput.content === 'object') {
    return {
      site: siteInput,
      content: {
        slots: Array.isArray(siteInput.content.slots) ? siteInput.content.slots : [],
        navigation: Array.isArray(siteInput.content.navigation) ? siteInput.content.navigation : []
      }
    };
  }

  return {
    site: { content: siteInput },
    content: {
      slots: Array.isArray(siteInput.slots) ? siteInput.slots : [],
      navigation: Array.isArray(siteInput.navigation) ? siteInput.navigation : []
    }
  };
}

// FormEdit Tree uses BFName || type. Page-schema norm: BFName for tree,
// `label` only when VFG should show a visible label. Chrome html/groups omit label.

// FormEdit / BFDraggable locators — same shape as page schema fields.
function makeIdbf (kind) {
  const suffix = Math.random().toString(36).slice(2, 8);
  return `idbf_${kind}_${suffix}`;
}

function mergeFieldAttributes (attributes) {
  if (!attributes || typeof attributes !== 'object') {
    return {};
  }

  const merged = Object.assign({}, attributes);
  if (attributes.formElement && typeof attributes.formElement === 'object') {
    merged.formElement = Object.assign({}, attributes.formElement);
  }
  if (attributes.formGroup && typeof attributes.formGroup === 'object') {
    merged.formGroup = Object.assign({}, attributes.formGroup);
  }
  return merged;
}

function mergeClassNames (...parts) {
  const seen = new Set();
  const out = [];
  parts.forEach((part) => {
    String(part || '').split(/\s+/).forEach((cls) => {
      if (!cls || seen.has(cls)) return;
      seen.add(cls);
      out.push(cls);
    });
  });
  return out.join(' ');
}

function findMatchingCloseIndex (tag, rest) {
  const token = new RegExp(`<${tag}(?:\\s[\\s\\S]*?)?\\/?>|<\\/${tag}>`, 'gi');
  let depth = 1;
  let match;
  while ((match = token.exec(rest))) {
    const isClose = match[0].slice(0, 2) === '</';
    const isSelfClose = /\/\s*>$/.test(match[0]);
    if (isClose) {
      depth -= 1;
      if (depth === 0) return match.index;
    } else if (!isSelfClose) {
      depth += 1;
    }
  }
  return -1;
}

function takeQuotedAttr (attrSrc, name) {
  const re = new RegExp(`(?:^|\\s)${name}\\s*=\\s*(["'])([\\s\\S]*?)\\1`, 'i');
  const match = attrSrc.match(re);
  if (!match) return { value: undefined, rest: attrSrc };
  return {
    value: match[2],
    rest: attrSrc.replace(re, ' ')
  };
}

function peelRootVisibility (attrSrc) {
  const vif = takeQuotedAttr(attrSrc, 'v-if');
  const vshow = takeQuotedAttr(vif.rest, 'v-show');

  if (vif.value !== undefined && vshow.value !== undefined) {
    return {
      visible_calc: `(${vif.value}) && (${vshow.value})`,
      attrSrc: vshow.rest
    };
  }

  return {
    visible_calc: vif.value !== undefined ? vif.value : vshow.value,
    attrSrc: vshow.rest
  };
}

function hasUnbalancedQuotes (text) {
  let count = 0;
  for (const ch of String(text)) {
    if (ch === '"') count += 1;
  }
  return count % 2 !== 0;
}

function splitSingleRootElement (html) {
  const trimmed = String(html == null ? '' : html).trim();
  const openMatch = trimmed.match(/^<([A-Za-z][\w:-]*)(\s[\s\S]*?)?(\/)?>/);
  if (!openMatch) return null;
  // A `>` inside a quoted attribute makes this regex stop early, mangling
  // attrSrc (e.g. `title="a>`); bail rather than hoist a corrupt root.
  if (hasUnbalancedQuotes(openMatch[0])) return null;

  const tag = openMatch[1];
  const tagLower = tag.toLowerCase();
  if (!HOISTABLE_TAGS.has(tagLower)) return null;

  const peeled = peelRootVisibility(openMatch[2] || '');
  const attrSrc = peeled.attrSrc;
  if (/(?:^|\s)(?:v-[\w-]+|@[\w.-]+|:[\w.-]+|style)\s*=/i.test(attrSrc)) return null;

  const selfClosing = Boolean(openMatch[3]) || /\/\s*>$/.test(openMatch[0]);
  let inner = '';
  if (selfClosing) {
    if (openMatch[0].length !== trimmed.length) return null;
  } else {
    const rest = trimmed.slice(openMatch[0].length);
    const closeIdx = findMatchingCloseIndex(tag, rest);
    if (closeIdx < 0) return null;
    const closeLen = tag.length + 3;
    if (rest.slice(closeIdx + closeLen).trim()) return null;
    inner = rest.slice(0, closeIdx).trim();
  }

  const classMatch = attrSrc.match(/\sclass\s*=\s*(["'])([\s\S]*?)\1/i);
  const idMatch = attrSrc.match(/\sid\s*=\s*(["'])([\s\S]*?)\1/i);
  return {
    html: inner,
    styleClasses: classMatch ? classMatch[2].trim() : '',
    id: idMatch ? idMatch[2].trim() : '',
    visible_calc: peeled.visible_calc
  };
}

// Vue 2 drops every root-level node after the first at render time.
// Hoisting a slot's root div (or leaving an interactive root in place) can
// leave multi-node fragment html — detect it so it can be wrapped once.
// Single text nodes are valid single roots and must NOT count as fragments.
const VOID_TAGS = new Set(['img', 'br', 'hr', 'input', 'meta', 'link', 'source', 'col', 'area', 'base', 'embed', 'track', 'wbr']);

function countTopLevelNodes (html) {
  const src = String(html == null ? '' : html).trim();
  let i = 0;
  let count = 0;

  while (i < src.length && count < 2) {
    while (i < src.length && /\s/.test(src[i])) i += 1;
    if (i >= src.length) break;

    if (src.startsWith('<!--', i)) {
      const end = src.indexOf('-->', i);
      i = end === -1 ? src.length : end + 3;
      continue; // comments are stripped by Vue 2 — never count
    }

    if (src[i] === '<') {
      const openMatch = src.slice(i).match(/^<([A-Za-z][\w:-]*)(\s[\s\S]*?)?(\/)?>/);
      if (!openMatch) {
        count += 1;
        i += 1;
        continue;
      }
      const tagLower = openMatch[1].toLowerCase();
      const selfClosing = Boolean(openMatch[3]) || /\/\s*>$/.test(openMatch[0]);
      if (selfClosing || VOID_TAGS.has(tagLower)) {
        i += openMatch[0].length;
      } else {
        const rest = src.slice(i + openMatch[0].length);
        const closeIdx = findMatchingCloseIndex(tagLower, rest);
        i = closeIdx < 0
          ? src.length
          : i + openMatch[0].length + closeIdx + tagLower.length + 3;
      }
      count += 1;
      continue;
    }

    const nextLt = src.indexOf('<', i);
    i = nextLt === -1 ? src.length : nextLt;
    count += 1; // text run
  }

  return count;
}

function isFragmentHtml (html) {
  return countTopLevelNodes(html) > 1;
}

function wrapFragmentHtml (html) {
  return `<div style="display: contents;">${String(html == null ? '' : html).trim()}</div>`;
}

const TW_CHROME_SPACER = 'mb-0';

function prepareChromeHtml (html, { cellClasses, slotClasses } = {}) {
  const hoisted = splitSingleRootElement(html);
  const rawHtml = hoisted ? hoisted.html : (html || '');
  // Wrap before mergeClassNames so the spacer lands on the field, not the wrapper.
  const sourceHtml = isFragmentHtml(rawHtml) ? wrapFragmentHtml(rawHtml) : rawHtml;
  const styleClasses = mergeClassNames(
    cellClasses,
    TW_CHROME_SPACER,
    hoisted && hoisted.styleClasses,
    slotClasses
  );
  const attributes = {};
  if (hoisted && hoisted.id) {
    attributes.formElement = { id: hoisted.id };
  }
  return {
    html: sourceHtml,
    styleClasses,
    attributes,
    visible_calc: hoisted ? hoisted.visible_calc : undefined
  };
}

function groupField({ label, fields, visible_calc, styleClasses, styleClasses_calc, tag, attributes }) {
  const attrs = mergeFieldAttributes(attributes);
  if (!attrs.formGroup || typeof attrs.formGroup !== 'object') {
    attrs.formGroup = {};
  }
  if (!attrs.formGroup['data-idbf']) {
    attrs.formGroup['data-idbf'] = makeIdbf('g');
  }

  const field = {
    type: 'group',
    BFName: label,
    styleClasses: typeof styleClasses === 'string' ? styleClasses : '',
    attributes: attrs,
    fields
  };

  if (visible_calc !== undefined) field.visible_calc = visible_calc;
  if (typeof styleClasses_calc === 'string') field.styleClasses_calc = styleClasses_calc;
  if (tag) field.tag = tag;

  return field;
}

function htmlField({
  label,
  html,
  visible_calc,
  styleClasses,
  divWrapper = false,
  attributes
}) {
  const attrs = mergeFieldAttributes(attributes);
  if (!attrs.formElement || typeof attrs.formElement !== 'object') {
    attrs.formElement = {};
  }
  if (!attrs.formElement['data-idbf']) {
    attrs.formElement['data-idbf'] = makeIdbf('e');
  }

  const field = {
    type: 'html',
    BFName: label,
    html,
    divWrapper,
    attributes: attrs
  };

  if (visible_calc !== undefined) field.visible_calc = visible_calc;

  field.styleClasses = typeof styleClasses === 'string' ? styleClasses : '';

  return field;
}

function isEmptyChromeHtml(html) {
  if (html == null) return true;
  const trimmed = String(html).trim();
  if (!trimmed) return true;
  // Only drop tag-with-no-attrs-and-no-body. Keep <div class="flex-1"></div>
  // — those still generate a layout box.
  return /^<(div|span|p|li)>\s*<\/\1>$/i.test(trimmed);
}

function slotsForName(slots, slotName) {
  return slots.filter((slot) => slot && slot.slot === slotName);
}

function slotFieldLabel(slotName, index, total) {
  if (total <= 1) return slotName;
  return `${slotName} — ${index + 1}`;
}

function buildSlotFields(slotName, slots, report, cellClasses) {
  const matches = slotsForName(slots, slotName);
  const kept = matches.filter((slot) => !isEmptyChromeHtml(slot.html));

  return kept.map((slot, index) => {
    report.slotsConverted.push({
      slot: slotName,
      index,
      bfId: slot.bfId || null
    });

    const prepared = prepareChromeHtml(slot.html || '', {
      cellClasses,
      slotClasses: slot.styleClasses
    });

    return htmlField({
      label: slotFieldLabel(slotName, index, kept.length),
      html: prepared.html,
      styleClasses: prepared.styleClasses,
      attributes: prepared.attributes,
      visible_calc: prepared.visible_calc
    });
  });
}

function buildDefaultField(slotName, report) {
  const template = DEFAULT_FIELD_TEMPLATES[slotName];
  if (!template) return null;

  report.defaultsMaterialized.push(slotName);

  return htmlField({
    label: template.label,
    html: template.html,
    visible_calc: template.visible_calc,
    styleClasses: template.styleClasses
  });
}

function buildHeaderRegionFields(slotName, slots, report) {
  const matches = slotsForName(slots, slotName);
  if (matches.length > 0) {
    return buildSlotFields(slotName, slots, report, TW_HEADER_CELL);
  }

  const defaultField = buildDefaultField(slotName, report);
  return defaultField ? [defaultField] : [];
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function serializeActions(actions) {
  return JSON.stringify(actions).replace(/'/g, "\\'");
}

function resolveVisibleCalc(source) {
  if (!source || typeof source !== 'object') return undefined;
  if (source.visible_calc) return source.visible_calc;
  if (source.visible === false) return 'false';
  return undefined;
}

function navItemVisibilityAttr(item) {
  if (item.visible_calc) {
    return ` v-if="${item.visible_calc}"`;
  }
  if (item.visible === false) {
    return ' v-if="false"';
  }
  return '';
}

function navItemToHtml(item) {
  if (!item || typeof item !== 'object') return '';

  if (item.subs && item.subs.length) {
    const subItems = item.subs.map((sub) => navItemToHtml(sub)).join('');
    return [
      `<li class="nav-dropdown ${item.styleClasses || ''}"${navItemVisibilityAttr(item)}>`,
      `<a href="#" title="${escapeHtml(item.label || '')}"><i class="${item.icon || ''}"></i> ${escapeHtml(item.label || '')}</a>`,
      `<ul class="nav nav-pills nav-stacked nav-sub">${subItems}</ul>`,
      '</li>'
    ].join('');
  }

  if (item.actions) {
    const actionsLiteral = serializeActions(item.actions);
    return [
      `<li class="${item.styleClasses || ''}"${navItemVisibilityAttr(item)}>`,
      `<a href="#" title="${escapeHtml(item.label || '')}" @click.prevent="action(${actionsLiteral})">`,
      `<i class="${item.icon || ''}"></i> ${escapeHtml(item.label || '')}`,
      '</a>',
      '</li>'
    ].join('');
  }

  if (item.html) {
    return `<li class="${item.styleClasses || ''}"${navItemVisibilityAttr(item)}>${item.html}</li>`;
  }

  if (item.path) {
    const exactAttr = item.exact ? ' :exact="true"' : '';
    const pathLiteral = String(item.path).startsWith("'") || String(item.path).startsWith('"')
      ? item.path
      : `'${String(item.path).replace(/'/g, "\\'")}'`;
    return [
      `<router-link tag="li" :to="${pathLiteral}"${exactAttr}${navItemVisibilityAttr(item)}`,
      ` title="${escapeHtml(item.label || '')}" class="${item.styleClasses || ''}" active-class="active">`,
      `<a><i class="${item.icon || ''}"></i> ${escapeHtml(item.label || '')}</a>`,
      '</router-link>'
    ].join('');
  }

  return '';
}

function navigationSectionToHtml(section) {
  const navClasses = [
    'nav-bar',
    section.styleClassesSection || ''
  ].filter(Boolean).join(' ');
  const ulClasses = [
    'nav',
    'nav-pills',
    'nav-stacked',
    section.styleClasses || ''
  ].filter(Boolean).join(' ');
  const heading = section.sectionLabel
    ? `<h5 class="sidebar-header">${escapeHtml(section.sectionLabel)}</h5>`
    : '';
  const items = (section.subs || []).map((item) => navItemToHtml(item)).join('');

  return [
    `<nav class="${navClasses}">`,
    heading,
    `<ul class="${ulClasses}">${items}</ul>`,
    '</nav>'
  ].join('');
}

function navigationSectionLabel(section, sectionIndex) {
  const name = section.sectionLabel || `Section ${sectionIndex + 1}`;
  return `Navigation — ${name}`;
}

function buildHeaderGroup(site, slots, report) {
  const fields = [];

  // Match headerblock.vue slot order: brand, then left nav (hamburger), center, right.
  HEADER_SLOT_ORDER.forEach((slotName) => {
    if (slotName === 'headerSidebarToggle' || slotName === 'headerLeft') {
      return;
    }

    if (slotName === 'headerCenter' || slotName === 'headerRight' || slotName === 'logout') {
      return;
    }

    fields.push(...buildHeaderRegionFields(slotName, slots, report));
  });

  const leftNavFields = [
    ...buildHeaderRegionFields('headerSidebarToggle', slots, report),
    ...buildSlotFields('headerLeft', slots, report, TW_HEADER_CELL)
  ];

  if (leftNavFields.length) {
    fields.push(groupField({
      label: 'Header Left Nav',
      tag: 'ul',
      styleClasses: TW_HEADER_NAV,
      fields: leftNavFields
    }));
  }

  fields.push(...buildSlotFields('headerCenter', slots, report, TW_HEADER_CELL));

  const headerRightFields = buildSlotFields('headerRight', slots, report, TW_HEADER_CELL);
  const logoutFields = buildHeaderRegionFields('logout', slots, report);
  const rightNavFields = [
    ...headerRightFields,
    ...logoutFields
  ];

  if (rightNavFields.length) {
    const brandFillsHeader = fields.some((field) =>
      /\bflex-1\b|\bflex-auto\b|\bml-auto\b/.test(field.styleClasses || '')
    );
    if (headerRightFields.length > 0 || !brandFillsHeader) {
      fields.push(groupField({
        label: 'Header Right Nav',
        tag: 'ul',
        styleClasses: TW_HEADER_NAV_RIGHT,
        fields: rightNavFields
      }));
    } else {
      fields.push(...rightNavFields);
    }
  }

  return groupField({
    label: 'Site Header',
    tag: 'header',
    styleClasses: TW_HEADER_BAR,
    // VFG binds attributes.formGroup || attributes — keep id on formGroup so
    // leftover site CSS targeting #header still applies until those rules go.
    attributes: {
      formGroup: {
        id: 'header'
      }
    },
    visible_calc: VISIBLE_TOP_NAV,
    fields
  });
}

function buildSecondaryHeaderGroup(slots, report) {
  const headerSlots = slotsForName(slots, 'header');
  if (!headerSlots.length) return null;

  const fields = headerSlots.map((slot, index) => {
    report.slotsConverted.push({
      slot: 'header',
      index,
      bfId: slot.bfId || null
    });

    const prepared = prepareChromeHtml(slot.html || '', { slotClasses: slot.styleClasses });
    return htmlField({
      label: slotFieldLabel('header', index, headerSlots.length),
      html: prepared.html,
      styleClasses: prepared.styleClasses,
      attributes: prepared.attributes
    });
  });

  return groupField({
    label: 'Secondary Header',
    tag: 'div',
    styleClasses: TW_SECONDARY_HEADER,
    visible_calc: VISIBLE_TOP_NAV,
    fields
  });
}

function buildSidebarGroup(site, slots, navigation, report) {
  const fields = [];

  SIDEBAR_SLOT_ORDER.forEach((slotName) => {
    if (slotName === 'sidebarLeftTop') {
      fields.push(...buildSlotFields(slotName, slots, report));
      return;
    }
  });

  navigation.forEach((section, sectionIndex) => {
    fields.push(htmlField({
      label: navigationSectionLabel(section, sectionIndex),
      html: navigationSectionToHtml(section),
      visible_calc: resolveVisibleCalc(section)
    }));
  });

  SIDEBAR_SLOT_ORDER.forEach((slotName) => {
    if (slotName === 'sidebarLeftTop') return;
    fields.push(...buildSlotFields(slotName, slots, report));
  });

  return groupField({
    label: 'Sidebar',
    tag: 'aside',
    styleClasses: TW_SIDEBAR,
    styleClasses_calc: STYLE_CLASSES_SIDEBAR_CALC,
    visible_calc: VISIBLE_SIDE_NAV,
    fields
  });
}

function buildRouterViewField() {
  return {
    type: 'routerView',
    BFName: 'Page Body',
    styleClasses: TW_PAGE_BODY,
    attributes: {
      formElement: {
        'data-idbf': makeIdbf('e')
      }
    }
  };
}

function buildFooterGroup(slots, unknownSlots, report) {
  const fields = [];

  slotsForName(slots, 'appFooter').forEach((slot, index) => {
    report.slotsConverted.push({
      slot: 'appFooter',
      index,
      bfId: slot.bfId || null
    });

    const prepared = prepareChromeHtml(slot.html || '', { slotClasses: slot.styleClasses });
    fields.push(htmlField({
      label: slotFieldLabel('appFooter', index, slotsForName(slots, 'appFooter').length),
      html: prepared.html,
      styleClasses: prepared.styleClasses,
      attributes: prepared.attributes
    }));
  });

  unknownSlots.forEach((entry) => {
    const prepared = prepareChromeHtml(entry.html || '', { slotClasses: entry.styleClasses });
    fields.push(htmlField({
      label: slotFieldLabel(entry.slot, entry.index, 1),
      html: prepared.html,
      styleClasses: prepared.styleClasses,
      attributes: prepared.attributes
    }));
  });

  if (!fields.length) return null;

  return groupField({
    label: 'App Footer',
    tag: 'footer',
    styleClasses: TW_APP_FOOTER,
    fields
  });
}

function collectPageSlotsPreserved(slots) {
  const counts = {};
  const order = [];

  slots.forEach((slot) => {
    if (!slot || !PAGE_SLOTS.has(slot.slot)) return;
    if (!counts[slot.slot]) {
      order.push(slot.slot);
      counts[slot.slot] = 0;
    }
    counts[slot.slot] += 1;
  });

  return order.map((slot) => ({
    slot,
    count: counts[slot]
  }));
}

function collectUnknownSlots(slots) {
  const unknown = [];

  slots.forEach((slot, index) => {
    if (!slot || !slot.slot) return;
    if (PAGE_SLOTS.has(slot.slot) || KNOWN_CHROME_SLOTS.has(slot.slot)) return;

    unknown.push({
      slot: slot.slot,
      index,
      html: slot.html || '',
      styleClasses: slot.styleClasses
    });
  });

  return unknown;
}

function convertSiteToGlobalSchema(siteInput, options = {}) {
  const { site, content } = normalizeSiteInput(siteInput);
  const slots = (content.slots || []).filter((slot) => slot && slot.slot !== 'inactive');
  const navigation = content.navigation;

  const report = {
    slotsConverted: [],
    pageSlotsPreserved: collectPageSlotsPreserved(slots),
    defaultsMaterialized: [],
    unmappedSlots: [],
    warnings: []
  };

  const unknownSlots = collectUnknownSlots(slots);
  report.unmappedSlots = unknownSlots.map((entry) => ({
    slot: entry.slot,
    index: entry.index,
    placement: 'appFooter'
  }));

  if (options.dryRun) {
    buildHeaderGroup(site, slots, report);
    buildSecondaryHeaderGroup(slots, report);
    buildSidebarGroup(site, slots, navigation, report);
    buildFooterGroup(slots, unknownSlots, report);

    return {
      globalSchema: null,
      report
    };
  }

  const shellFields = [
    buildHeaderGroup(site, slots, report)
  ];

  const secondaryHeader = buildSecondaryHeaderGroup(slots, report);
  if (secondaryHeader) shellFields.push(secondaryHeader);

  shellFields.push(groupField({
    label: 'Body',
    styleClasses: TW_BODY,
    fields: [
      buildSidebarGroup(site, slots, navigation, report),
      buildRouterViewField()
    ]
  }));

  const footer = buildFooterGroup(slots, unknownSlots, report);
  if (footer) shellFields.push(footer);

  const fields = [
    groupField({
      label: 'App Shell',
      styleClasses: TW_APP_SHELL,
      fields: shellFields
    })
  ];

  // Omit empty routes — runtime uses `globalSchema.routes || []`.
  // Prefer default schema + visible_calc for most chrome differences.
  const globalSchema = {
    id: options.schemaId || 'global-layout-from-legacy',
    version: 1,
    // Dev correlation only — not read by runtime
    _source: 'legacy-convert-v2',
    default: {
      schema: {
        fields
      }
    }
  };

  return {
    globalSchema,
    report
  };
}

export {
  convertSiteToGlobalSchema,
  HEADER_SLOT_ORDER,
  SIDEBAR_SLOT_ORDER,
  PAGE_SLOTS,
  KNOWN_CHROME_SLOTS,
  VISIBLE_TOP_NAV,
  VISIBLE_SIDE_NAV,
  VISIBLE_AUTH,
  STYLE_CLASSES_SIDEBAR_CALC
};
